/**
 * ★ VW (decisão do dono, 20/set/2026) — o que a tela diz sobre QUEM está com a
 * conversa. Puro, num lugar só: cabeçalho da thread, painel do contato, lista e
 * composer leem daqui.
 *
 * Encerrada NÃO tem dono: o servidor limpa `responsavelId` ao encerrar. Enquanto
 * as encerradas anteriores ao degrau ainda carregarem um dono antigo, a tela
 * decide pelo STATUS primeiro — nunca mostra "Em atendimento por X" numa
 * encerrada. Quem encerrou fica em `resolvidaPorNome`.
 */

/** A conversa tem um dono a mostrar? Só quando está ABERTA e alguém a assumiu. */
export const mostraDono = (conversa) =>
  Boolean(conversa) && conversa.status !== 'resolvida' && Boolean(conversa.responsavelId);

/** Rótulo do estado de atendimento, para cabeçalho e painel. */
export function rotuloDoAtendimento(conversa) {
  if (!conversa) return '';
  if (conversa.ehGrupo) return 'Grupo de WhatsApp';
  if (conversa.status === 'resolvida') {
    return conversa.resolvidaPorNome ? `Encerrada por ${conversa.resolvidaPorNome}` : 'Encerrada';
  }
  if (conversa.responsavelId) return `Em atendimento por ${conversa.responsavelNome || 'atendente'}`;
  return 'Aguardando atendimento';
}

/**
 * ★★★ A1/E1 (decisão do dono, 09/out/2026) — QUEM PODE ENVIAR NESTA CONVERSA. Puro.
 *
 * ★★ O ESPELHO da guarda do servidor (`functions/lib/guardas.js` →
 * `exigirEmAtendimentoPor`). ⚠⚠ A autoridade é o servidor; esta função existe para a tela
 * **explicar antes** em vez de deixar a pessoa levar um erro depois de escrever a mensagem.
 *
 * ★ `'grupo'` é separado de `'pode'` de propósito: ⚠ num grupo não há atendente, e a tela não
 * deve oferecer "Assumir" ali.
 *
 * @returns `'pode'` · `'grupo'` · `'aguardando'` (sem dono) · `'de_outro'` · `'encerrada'`
 */
export function estadoDoEnvio(conversa, meuUid) {
  if (!conversa) return 'aguardando';
  if (conversa.ehGrupo === true) return 'grupo';
  // ★★★ Espelho exato do servidor: **só `aberta` segue adiante** (ver `exigirEmAtendimentoPor`).
  // ⚠ Um status inesperado trava e oferece "Reabrir" — ★ é o gesto certo para o que não está aberto.
  if (conversa.status !== 'aberta') return 'encerrada';
  if (!conversa.responsavelId) return 'aguardando';
  return conversa.responsavelId === meuUid ? 'pode' : 'de_outro';
}

/** ★ Pode mandar mensagem para o paciente? ⚠ Nota interna NÃO passa por aqui — é liberada. */
export const podeEnviar = (conversa, meuUid) => {
  const e = estadoDoEnvio(conversa, meuUid);
  return e === 'pode' || e === 'grupo';
};

/**
 * ★★ O texto que substitui a caixa, e o rótulo do botão.
 *
 * ⚠ **"Reabrir" e não "Assumir" na encerrada**: ★ reabrir é o gesto que existe na tela para
 * aquele estado, e pedir "assuma" numa conversa encerrada mandaria a pessoa procurar um botão
 * que não está ali.
 */
export const CONVITE_ENVIO = {
  aguardando: { texto: 'Para responder, assuma a conversa', acao: 'Assumir' },
  de_outro: { texto: 'Para responder, assuma a conversa', acao: 'Assumir' },
  encerrada: { texto: 'Esta conversa está encerrada. Reabra para responder', acao: 'Reabrir' },
};
